import { useState, useEffect } from "react";

// Boolean codec: the historical shape — getItem(key)==="true", written back as "true"/"false".
const BOOL_CODEC = { read: (raw) => raw === "true", write: (v) => (v ? "true" : "false") };
// JSON codec for object-valued persisted state (e.g. tracking prefs).
export const JSON_CODEC = { read: (raw) => JSON.parse(raw), write: (v) => JSON.stringify(v) };

// Default storage provider: localStorage, never throwing on access (SSR / private
// mode / sandboxed contexts). Mirrors defaultSessionStorage in lib/util/sessionStorage.js.
export const defaultLocalStorage = () => {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
};

/**
 * Read persisted state through a `storage` provider (`() => Storage|null`). Returns
 * `fallback` when storage is unavailable, the key is absent, or the codec throws.
 * @template T
 * @param {() => (Storage|null)} storage
 * @param {string} key
 * @param {T} fallback
 * @param {{read:(raw:string)=>T, write:(v:T)=>string}} codec
 * @returns {T}
 */
export function readPersisted(storage, key, fallback, codec) {
  try {
    const s = storage();
    if (!s) return fallback;
    const raw = s.getItem(key);
    return raw === null ? fallback : codec.read(raw);
  } catch {
    return fallback;
  }
}

/** Write persisted state through a `storage` provider; no-op (never throws) when unavailable. */
export function writePersisted(storage, key, value, codec) {
  try {
    const s = storage();
    if (s) s.setItem(key, codec.write(value));
  } catch {
    /* private mode / quota / unavailable */
  }
}

/**
 * useState whose value is lazily seeded from `storage`[key] and persisted back
 * on every change. Replaces the repeated `useState(()=>storage.read) +
 * useEffect(()=>storage.write)` pairs in App.jsx with identical semantics:
 *  - init: missing key (getItem === null) → `fallback`; otherwise codec.read(raw);
 *    any throw (unavailable / bad value) → `fallback`.
 *  - persist: writes codec.write(value) on mount and on every value change.
 * @template T
 * @param {string} key  storage key
 * @param {T} fallback  value when the key is absent or unreadable
 * @param {{read:(raw:string)=>T, write:(v:T)=>string}} [codec]  defaults to BOOL_CODEC
 * @param {() => (Storage|null)} [storage]  storage provider; defaults to localStorage.
 *   Pass `defaultSessionStorage` (lib/util/sessionStorage) for tab-lifetime state (e.g. lastObs).
 * @returns {[T, import('react').Dispatch<import('react').SetStateAction<T>>]}
 */
export function usePersistedState(
  key,
  fallback,
  codec = BOOL_CODEC,
  storage = defaultLocalStorage,
) {
  const [value, setValue] = useState(() => readPersisted(storage, key, fallback, codec));
  useEffect(() => {
    writePersisted(storage, key, value, codec);
  }, [key, value, codec, storage]);
  return [value, setValue];
}
